import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { observer } from 'mobx-react-lite';
import { EditText } from '../../../../component/ui/edit-text/edit-text';
import globalSettingsBranches from '../../../../stores/global-settings-branches';
import { AccountTypeEnum } from '../../../../api/types/settings/accounting/chart-of-accounts/chart-of-accounts.type';
import { CardBrandType } from '../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import { ChartOfAccountDetailsEntity } from '../../../../api/types/settings/accounting/chart-of-accounts/chart-of-account-details.type';
import { updateChartOfAccountOperation } from '../operations/update-chart-of-account-operation';

interface EditChartOfAccountDialogProps {
  visible: boolean;
  account: ChartOfAccountDetailsEntity | null;
  onHide: () => void;
  onUpdated: () => void;
}

const accountTypeOptions = [
  { label: 'מזומן', value: AccountTypeEnum.CASH },
  { label: 'שיק', value: AccountTypeEnum.CHECK },
  { label: 'בנק', value: AccountTypeEnum.BANK },
  { label: 'אשראי', value: AccountTypeEnum.CREDIT_CARD },
];

const cardBrandOptions = [
  { label: 'Visa', value: CardBrandType.VISA },
  { label: 'Mastercard', value: CardBrandType.MASTERCARD },
  { label: 'Isracard', value: CardBrandType.ISRACARD },
  { label: 'American Express', value: CardBrandType.AMERICAN_EXPRESS },
  { label: 'Diners Club', value: CardBrandType.DINERS_CLUB },
  { label: 'Discover', value: CardBrandType.DISCOVER },
  { label: 'JCB', value: CardBrandType.JCB },
  { label: 'Maestro', value: CardBrandType.MAESTRO },
  { label: 'Private label', value: CardBrandType.PRIVATE_LABEL },
];

export const EditChartOfAccountDialog: React.FC<EditChartOfAccountDialogProps> =
  observer(({ visible, account, onHide, onUpdated }) => {
    const [accountCode, setAccountCode] = useState('');
    const [accountName, setAccountName] = useState('');
    const [accountType, setAccountType] = useState<AccountTypeEnum | null>(
      null,
    );
    const [creditCardCompany, setCreditCardCompany] =
      useState<CardBrandType | null>(null);
    const [bankName, setBankName] = useState('');
    const [branchIds, setBranchIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (visible && account) {
        setAccountCode(account.accountCode ?? '');
        setAccountName(account.accountName ?? '');
        setAccountType(account.accountType ?? null);
        setCreditCardCompany(account.creditCardCompany ?? null);
        setBankName(account.bankName ?? '');
        setBranchIds(
          account.branches ? account.branches.map((b) => b.branchId) : [],
        );
        setLoading(false);
      }
    }, [visible, account]);

    const handleAccountTypeChange = (next: AccountTypeEnum | null) => {
      setAccountType(next);
      if (next !== AccountTypeEnum.CREDIT_CARD) {
        setCreditCardCompany(null);
      }
      if (next !== AccountTypeEnum.BANK) {
        setBankName('');
      }
    };

    const handleSubmit = async () => {
      if (!account || !accountType) {
        return;
      }
      await updateChartOfAccountOperation(
        account.id,
        {
          accountCode: accountCode.trim(),
          accountName: accountName.trim(),
          accountType,
          creditCardCompany:
            accountType === AccountTypeEnum.CREDIT_CARD
              ? creditCardCompany
              : null,
          bankName:
            accountType === AccountTypeEnum.BANK
              ? bankName.trim() || null
              : null,
          branchIds,
        },
        setLoading,
        () => {
          onUpdated();
          onHide();
        },
      );
    };

    const branchOptions = globalSettingsBranches.branches.map((b) => ({
      label: b.branchName,
      value: b.id,
    }));

    const footer = (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
        }}
      >
        <Button
          label="ביטול"
          icon="pi pi-times"
          onClick={onHide}
          className="p-button-text"
          disabled={loading}
        />
        <Button
          label="עדכון"
          icon="pi pi-check"
          onClick={handleSubmit}
          loading={loading}
          disabled={loading || !account}
        />
      </div>
    );

    return (
      <Dialog
        header={
          account ? `עריכת חשבון - ${account.accountName}` : 'עריכת חשבון'
        }
        visible={visible}
        onHide={onHide}
        style={{ width: '560px', maxWidth: '95vw' }}
        footer={footer}
        dismissableMask
        draggable={false}
        resizable={false}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            direction: 'rtl',
            paddingTop: '4px',
          }}
        >
          <div style={{ display: 'flex', gap: '16px' }}>
            <EditText
              title="קוד חשבון"
              value={accountCode}
              onChange={setAccountCode}
              maxLength={20}
            />
            <EditText
              title="שם חשבון"
              value={accountName}
              onChange={setAccountName}
              maxLength={100}
            />
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '16px',
            }}
          >
            <label style={{ fontSize: '14px', color: '#4b5563' }}>
              סוג חשבון
            </label>
            <Dropdown
              value={accountType}
              options={accountTypeOptions}
              onChange={(e) => handleAccountTypeChange(e.value)}
              placeholder="בחר סוג חשבון"
              style={{
                height: '36px',
                marginTop: '4px',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            />
          </div>

          {accountType === AccountTypeEnum.CREDIT_CARD && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '16px',
              }}
            >
              <label style={{ fontSize: '14px', color: '#4b5563' }}>
                חברת אשראי
              </label>
              <Dropdown
                value={creditCardCompany}
                options={cardBrandOptions}
                onChange={(e) => setCreditCardCompany(e.value)}
                placeholder="בחר חברת אשראי"
                showClear
                style={{
                  height: '36px',
                  marginTop: '4px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              />
            </div>
          )}

          {accountType === AccountTypeEnum.BANK && (
            <EditText
              title="שם הבנק"
              value={bankName}
              onChange={setBankName}
              maxLength={50}
            />
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '16px',
            }}
          >
            <label style={{ fontSize: '14px', color: '#4b5563' }}>שלוחות</label>
            <MultiSelect
              value={branchIds}
              options={branchOptions}
              onChange={(e) => setBranchIds(e.value as number[])}
              placeholder="בחר שלוחות"
              maxSelectedLabels={3}
              selectedItemsLabel={`${branchIds.length} שלוחות נבחרו`}
              style={{
                height: '36px',
                marginTop: '4px',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            />
          </div>
        </div>
      </Dialog>
    );
  });
